import React, { ReactNode } from 'react';
import clsx from 'clsx';
import {
    MdAltRoute,
    MdBookmark,
    MdCheckCircle,
    MdEdit,
    MdEmojiEvents,
    MdExpandMore,
    MdRadioButtonUnchecked,
} from 'react-icons/md';
import { IOffer, IOfferAction } from '@models/winnerSelection/ITableDemand';

interface IProps {
    offer: IOffer;
    /** Доступна ли оценка (есть видимый RateOffer-action). Если нет — полоса
     *  показывает пассивное «не выбрано» без клика (право/кнопка отсутствуют). */
    canRate: boolean;
    /** RateOffer-action для открытия модалки «Оценить» (когда canRate). */
    rateAction?: IOfferAction;
    /** Открыть модалку выбора победителя (проксирует modals.open('rate', …)). */
    onSelectWinner?: (offer: IOffer, action: IOfferAction) => void;
}

interface IResultMeta {
    cls: string;
    label: string;
    icon: ReactNode;
}

/**
 * Оформление полосы по значению offer.result. Имена --main/--alt/--res —
 * цветовые, не дословно значение: Основной → зелёный,
 * Резервный/Альтернативный → синий, Одобренный после теста → графит.
 */
const resultMeta = (result: string): IResultMeta => {
    switch (result.toLowerCase()) {
        case 'основной':
            return {
                cls: 'ws-decbar--main',
                label: 'Основной победитель',
                icon: <MdEmojiEvents size="16px" aria-hidden />,
            };
        case 'резервный':
            return {
                cls: 'ws-decbar--alt',
                label: 'Резервный победитель',
                icon: <MdBookmark size="16px" aria-hidden />,
            };
        case 'альтернативный':
            return {
                cls: 'ws-decbar--alt',
                label: 'Альтернативный победитель',
                icon: <MdAltRoute size="16px" aria-hidden />,
            };
        case 'одобренный после теста':
            return {
                cls: 'ws-decbar--res',
                label: 'Одобрен после теста',
                icon: <MdCheckCircle size="16px" aria-hidden />,
            };
        default:
            return {
                cls: 'ws-decbar--res',
                label: result,
                icon: <MdCheckCircle size="16px" aria-hidden />,
            };
    }
};

/**
 * Полоса-решение в шапке предложения — единственное место, где на экране
 * появляется слово «победитель». Заменяет прежнюю result-пилюлю и заполняет
 * «вакуум» до выбора:
 * - есть result → цветная полоса статуса (+ «изменить», если оценка доступна);
 * - нет result, оценка доступна → активная кнопка «Выбрать победителя»
 *   (клик открывает модалку «Оценить», stopPropagation гасит меню шапки);
 * - нет result, оценка недоступна (нет прав / RateOffer невидим) → пассивная
 *   подпись без клика, развилка по «оценено ли предложение» (есть ли место/ранг):
 *   оценённое, но не выбранное — тихое «Не выбран победителем» (просто проигравший
 *   участник); совсем не оценённое — «Не оценено».
 *
 * Для отозванных предложений полоса не рендерится (см. OfferHeader — там
 * остаётся пилюля «Отозвано»).
 */
export const DecisionBar: React.FC<IProps> = ({ offer, canRate, rateAction, onSelectWinner }) => {
    const interactive = canRate && !!rateAction && !!onSelectWinner;

    const open = (event: React.MouseEvent) => {
        // Гасим всплытие: клик по полосе не должен открывать меню шапки
        // (щелчок по шапке открывает его в OfferHeaderMenuDialog).
        event.stopPropagation();
        if (interactive) onSelectWinner(offer, rateAction);
    };

    if (offer.result) {
        const meta = resultMeta(offer.result);
        const inner = (
            <>
                {meta.icon}
                <span className="ws-decbar__label">{meta.label}</span>
                {interactive ? (
                    <span className="ws-decbar__edit" aria-hidden>
                        <MdEdit size="12px" />
                        изменить
                    </span>
                ) : null}
            </>
        );
        // Когда оценка доступна — кликабельна вся полоса (не только «изменить»):
        // клик открывает модалку выбора, ховер даёт визуальный отклик (см. .scss).
        return interactive ? (
            <button
                type="button"
                className={clsx('ws-decbar', meta.cls)}
                onClick={open}
                title="Изменить решение по предложению"
            >
                {inner}
            </button>
        ) : (
            <div className={clsx('ws-decbar', meta.cls)}>{inner}</div>
        );
    }

    if (interactive) {
        return (
            <button
                type="button"
                className="ws-decbar ws-decbar--empty"
                onClick={open}
                aria-label="Оценить предложение"
            >
                <MdEmojiEvents size="16px" aria-hidden />
                <span className="ws-decbar__label">Оценить предложение</span>
                <MdExpandMore size="16px" aria-hidden className="ws-decbar__chev" />
            </button>
        );
    }

    // Оценено = назначено место (offer.rank). Оценённое, но не выбранное — просто
    // проигравший участник: исход уже сказан ранг-кружком, поэтому даём тихую
    // безрамочную подпись, а не пунктирное «не выбран» (пунктир читался бы как
    // активный слот под заполнение — то же, что «Оценить предложение»).
    const rankNum = offer.rank != null && offer.rank !== '' ? Number(offer.rank) : NaN;
    const isRated = !Number.isNaN(rankNum) && rankNum >= 1;

    if (isRated) {
        return (
            <div className="ws-decbar ws-decbar--lost">
                <span className="ws-decbar__dash" aria-hidden />
                <span className="ws-decbar__label">Не выбран победителем</span>
            </div>
        );
    }

    return (
        <div className="ws-decbar ws-decbar--unrated">
            <MdRadioButtonUnchecked size="16px" aria-hidden />
            <span className="ws-decbar__label">Не оценено</span>
        </div>
    );
};
